import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { DateTimePickerComponent } from '@syncfusion/ej2-react-calendars'
import { MultiSelectComponent } from '@syncfusion/ej2-react-dropdowns'
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'
import { useSelector } from 'react-redux'
import { EVENT_CATEGORIES } from '../../util/constants'
import CalendarSync from '../atoms/CalendarSync/CalendarSync.jsx'
import { useVendorOrPlannerSubscriptionAccess } from '../../hooks/index.js'
import EventTagsField from '../tags/EventTagsField'

const MAXBUDGET = 1000000000

export const EditorTemplate = (props) => {
	const { kazooId } = useParams()
	const partyMembers = useSelector((state) => state.kazooEvent.kazooMembers)
	const venues = useSelector((state) => state.kazooEvent.kazooVenues)
	const eventTagCatalog = useSelector((state) => state.guestList.eventTags)
	const [eventTags, setEventTags] = useState(() =>
		Array.isArray(props.EventTags) ? props.EventTags : []
	)
	const weddingVendors = useSelector((state) => state.vendor.vendors).filter(
		(vendor) => !vendor.status && vendor.__t === 'vendor'
	)
	const allVendors =
		useSelector((state) => state.vendor.vendorSearchList) || []
	const proposals = useSelector((state) => state.proposal.proposals)

	// Merge datasets, prioritizing weddingVendors (UserTimeline) but falling back/adding allVendors (VendorTimeline)
	// Use a Map to deduplicate by _id
	const vendorsMap = new Map()

	// Add allVendors first (from search list - these don't have __t property)
	allVendors.forEach((v) => {
		if (v._id) {
			vendorsMap.set(v._id, {
				...v,
				displayName: v.businessName || v.email || 'Unknown',
			})
		}
	})

	// Add weddingVendors (these have __t, firstName, lastName)
	weddingVendors.forEach((v) => {
		if (v._id) {
			vendorsMap.set(v._id, {
				...v,
				displayName:
					`${v.firstName || ''} ${v.lastName || ''}`.trim() ||
					v.businessName ||
					v.email ||
					'Unknown',
			})
		}
	})
	const paidVendor =
		proposals
			?.filter((val) => val.paymentStatus === 'paid')
			?.map((vendor) => ({
				...vendor,
				_id: vendor.vendorId,
				displayName: vendor.vendorName,
			})) ?? []
	const vendors = kazooId ? paidVendor : Array.from(vendorsMap.values())

	// Console log for vendors with their names
	const eventCategories = [
		'Pre-wedding',
		'Post-wedding',
		'Wedding (Party Only)',
		'Wedding (Guests and Party)',
	]

	// Determine if user is vendor
	const user = useSelector((state) => state.auth.user)
	const isVendor = user?.__t === 'vendor' || user?.type === 'vendor'
	const vendorStaff = useSelector((state) => state.vendor.staff) || []
	const subscriptionAccess = useVendorOrPlannerSubscriptionAccess()
	const isBusinessPro = useMemo(() => ['business-pro', 'business-pro-month'].includes(subscriptionAccess?.payment), [subscriptionAccess?.payment])

	// Logic moved below attendeesDataSource definition
	// Restrict editing if IS vendor AND IS Couple Event (has KazooId)
	// New events created by Vendor on this page won't have KazooId yet (or props.KazooId will be falsy)
	const isRestricted = isVendor && !!props.KazooId

	// Prepare attendees list based on role
	const vendorStaffData = [
		{ id: user._id, text: user.businessName || 'Me' },
		...(isBusinessPro ? vendorStaff
			.filter((s) => (s.staffId || s._id) && s.status === 'active')
			.map((s) => ({ id: s.staffId || s._id, text: s.name || s.email })) : []),
	]
	const members = [partyMembers?.bride, partyMembers?.groom]
	const allData = [
		{ id: 'guests', text: 'Guests' },
		...(members?.map((member) => {
			if (!member?.status && member?._id)
				return {
					id: member._id,
					text: `${member.preferredName || `${member.firstName} ${member.lastName}`}`,
				}
		}) ?? []),
	]?.filter((i) => i?.id)
	// If Vendor, restrict to vendorStaffData only (can only assign themselves/staff)
	// If not Vendor (Couple), allow all data (Guests/Bride/Groom)
	const attendeesDataSource = isVendor ? vendorStaffData : allData
	// Fetch full event from store to ensure we have OwnerIds
	const timelineEvents =
		useSelector((state) => state.scheduler.timelineEvents) || []
	const fullEvent = props._id
		? timelineEvents.find((e) => e._id === props._id)
		: null
	const rawOwnerIds = fullEvent?.Owners || props.Owners || []

	// Map IDs to objects { id, text }
	const currentOwnerIds = rawOwnerIds
		.filter((a) => a !== 'null' && a?.id !== 'null')
		.map((owner) => {
			// Handle if ID is already an object {id, text} (some legacy logic) or plain string
			const idStr = typeof owner === 'object' ? owner.id : String(owner)
			const match = attendeesDataSource.find(
				(m) => String(m.id) === String(idStr)
			)
			if (match) return { id: match.id, text: match.text } // Use matched object for consistent casing/types
			// If not found, check if original ID object had text, otherwise use ID as text
			const fallbackText =
				typeof owner === 'object' && owner.text ? owner.text : idStr
			return { id: idStr, text: fallbackText, value: fallbackText }
		})
		// If vendor, filter to only include vendorStaffData items
		.filter((owner) => {
			if (!isVendor) return true
			return vendorStaffData.some(
				(s) => String(s.id) === String(owner.id)
			)
		})
	// Ensure currently assigned owners are visible in the datasource so they display correctly
	// even if they wouldn't normally be in the list (e.g. Guests/Couple for a Vendor)
	const activeIds = new Set(attendeesDataSource?.map((i) => String(i.id)))

	// Filter out items that are already in the datasource
	const missingOwners = currentOwnerIds.filter(
		(owner) => !activeIds.has(String(owner.id))
	)

	// Create final datasource that includes the "hidden" but active owners
	// Show all data, but vendors can only change vendorStaffData items
	const finalDataSource = [...attendeesDataSource, ...missingOwners]

	// For vendors, create a restricted datasource where only vendorStaffData can be selected
	const selectableDataSource = isVendor ? vendorStaffData : finalDataSource

	return (
		<div className="custom-event-editor">
			<input
				className="e-field"
				type="hidden"
				name="_id"
				defaultValue={props._id}
			/>
			<table className="custom-event-editor">
				<tbody>
					<tr>
						<td className="e-textlabel">Events</td>
						<td colSpan={4}>
							<input
								id="event"
								className="e-field e-input"
								type="text"
								name="Subject"
								defaultValue={props.Subject}
								placeholder="Event name"
								disabled={isRestricted}
							/>
						</td>
					</tr>

					<tr>
						<td className="e-textlabel">From *</td>
						<td colSpan={4}>
							<DateTimePickerComponent
								id="startTime"
								format="dd/MM/yy hh:mm a"
								name="StartTime"
								value={new Date(props.StartTime)}
								className="e-field"
								placeholder="Start time and date"
								step={5}
								min={new Date()}
								enabled={!isRestricted}
							/>
						</td>
					</tr>

					<tr>
						<td className="e-textlabel">To *</td>
						<td colSpan={4}>
							<DateTimePickerComponent
								id="endTime"
								format="dd/MM/yy hh:mm a"
								name="EndTime"
								value={new Date(props.EndTime)}
								className="e-field"
								placeholder="End time and date"
								step={5}
								min={new Date()}
								enabled={!isRestricted}
							/>
						</td>
					</tr>

					<tr>
						<td className="e-textlabel">Location</td>
						<td colSpan={4}>
							<DropDownListComponent
								id="location"
								name="Location"
								className="e-field"
								dataSource={venues}
								placeholder="Select location"
								mode="Default"
								enabled={!isRestricted}
							/>
						</td>
					</tr>
					<tr>
						<td className="e-textlabel">Event Category *</td>
						<td colSpan={4}>
							<DropDownListComponent
								id="eventCategory"
								name="EventCategory"
								className="e-field"
								dataSource={EVENT_CATEGORIES}
								placeholder="Select event category"
								mode="Default"
								enabled={!isRestricted}
							/>
						</td>
					</tr>
					<tr>
						<td className="e-textlabel">Event Tags</td>
						<td colSpan={4}>
							<EventTagsField
								value={eventTags}
								onChange={setEventTags}
								catalog={eventTagCatalog}
								kazooId={kazooId}
							/>
							<input
								type="hidden"
								className="e-field"
								name="EventTags"
								value={JSON.stringify(eventTags)}
							/>
						</td>
					</tr>
					<tr>
						<td className="e-textlabel">Attendees *</td>
						<td colSpan={4}>
							<MultiSelectComponent
								key={props._id} // Force re-render when event changes
								id="ownerIds"
								name="OwnerIds"
								className="e-field"
								dataSource={selectableDataSource}
								fields={{ text: 'text', value: 'id' }}
								placeholder="Select attendees"
								mode="Default"
								data-cy="attendees-dropdown"
								popupHeight="200px"
								allowFiltering={true}
								value={currentOwnerIds.map((o) => o.id)}
							/>
						</td>
					</tr>

					<tr style={{ display: isVendor ? 'none' : '' }}>
						<td className="e-textlabel">Vendors</td>
						<td colSpan={4}>
							<MultiSelectComponent
								id="vendorIds"
								name="VendorIds"
								className="e-field"
								dataSource={vendors}
								fields={{ text: 'displayName', value: '_id' }}
								placeholder="Select vendors"
								mode="Default"
								data-cy="vendors-dropdown"
								popupHeight="200px"
								allowFiltering={true}
								enabled={!isRestricted}
							/>
						</td>
					</tr>

					<tr>
						<td className="e-textlabel">Calendar Sync</td>
						<td colSpan={4}>
							<CalendarSync
								formMode
								defaultValues={props.syncToCalendar}
								showLabel={false}
							/>
						</td>
					</tr>
					
					<tr>
						<td className="e-textlabel">Budget</td>
						<td colSpan={4}>
							<input
								id="budget"
								type="number"
								step="0.01"
								className="e-field e-input"
								name="Budget"
								min="0"
								defaultValue={props.Budget}
								onChange={(e) => {
									const newValue = parseFloat(e.target.value)
									e.target.value = isNaN(newValue)
										? 0
										: Math.max(
												0,
												Math.min(
													MAXBUDGET,
													newValue.toFixed(2)
												)
											)
								}}
								placeholder="Budget"
								disabled={isRestricted}
							/>
						</td>
					</tr>

					<tr>
						<td className="e-textlabel">Event Color</td>
						<td colSpan={4}>
							<div className="color-picker-wrapper">
								<DropDownListComponent
									id="eventColor"
									name="EventColor"
									data-name="EventColor"
									dataSource={[
										{ name: 'Default', color: '#dfeef4' },
										{
											name: 'Light Pink',
											color: '#FFC0CB',
										},
										{
											name: 'Light Green',
											color: '#98FB98',
										},
									]}
									fields={{ text: 'name', value: 'color' }}
									value={props.EventColor || '#dfeef4'}
									index={0}
									itemTemplate={(data) => (
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '8px',
												padding: '8px',
											}}
										>
											<span
												style={{
													display: 'inline-block',
													width: '18px',
													height: '18px',
													backgroundColor: data.color,
													border: '1px solid #ccc',
												}}
											></span>
											<span>{data.name}</span>
										</div>
									)}
									placeholder="Select event color"
									popupHeight="250px"
									className="e-field color-picker"
									enabled={!isRestricted}
								/>
							</div>
						</td>
					</tr>

					<tr>
						<td className="e-textlabel">Description</td>
						<td colSpan={4}>
							<textarea
								id="description"
								className="e-field e-input"
								name="Description"
								rows={3}
								cols={50}
								defaultValue={props.Description}
								placeholder="Description of the event"
								disabled={isRestricted}
							></textarea>
						</td>
					</tr>

					<tr>
						<td className="e-textlabel">Requirements</td>
						<td colSpan={4}>
							<textarea
								id="requirements"
								className="e-field e-input"
								name="Requirements"
								rows={3}
								cols={50}
								defaultValue={props.Requirements}
								placeholder="Requirements for the event"
								disabled={isRestricted}
							></textarea>
						</td>
					</tr>

					<tr>
						<td className="e-textlabel">All Day</td>
						<td colSpan={4}>
							<input
								type="checkbox"
								id="allDay"
								name="IsAllDay"
								className="e-field checkbox"
								disabled={isRestricted}
							/>
						</td>
					</tr>
					<tr>
						<td className="e-textlabel">Locked</td>
						<td colSpan={4}>
							<input
								type="checkbox"
								id="locked"
								name="Locked"
								className="e-field checkbox"
							/>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	)
}
